<template>
  <div class="keyword-tracking-root">
    <div v-if="keywordTrack?.trackResult?.length">
      <div class="pb-5 d-flex justify-space-between align-end gap20">

        <div style="flex: 1;">

          <div class="mb-8">
            <div class="mb-4" style="color: var(--v-gray50Color-base); margin-bottom: 6px; font-size: 1rem;">{{ site.name }}</div>
            <div class="card-big-title mb-4 d-flex gap10" style="color: var(--v-blackColor-base); font-size: 1.8rem; font-weight: 700;">
              Keyword tracking
              <span class="material-icons video-tutorial-icon video-tutorial-icon-size"
                @click="() => {
                  $store.commit('setCurrentVideoTutorialToDisplay', {
                    title: 'How to Use the Keyword Tracker Tool',
                    videoLink: 'https://www.youtube.com/embed/ZvKyAujQJfc',
                    description: 'In this video, you\'ll learn how to use POP\'s Keyword Tracker tool. The Keyword Tracker tool takes the guesswork out of monitoring your keyword progress without bogging you down with complicated dashboards. You\'ll receive straightforward updates on your keywords, including old rank, new rank, rank changes, monthly search volume, and the current ranking URL. Plus, with email updates, you\'re always in the loop about any ranking changes.',
                    time: '1m 43s'
                  });
                }">
                videocam
              </span>
            </div>
          </div>

          <div
            v-if="activeTab === 'lists' && !!selectedList"
            class="d-flex align-center gap5 mb-8"
          >
            <a href="#" @click.prevent="selectedList = null" style="font-size: 1rem; color: var(--v-mainColor3-base);">Lists</a>
            <i class="material-icons-outlined" style="color: var(--v-blackColor-base); font-size: 0.8rem; margin-top: 2px;">keyboard_arrow_right</i>
            <span style="font-weight: 700; font-size: 1rem; color: var(--v-mainColor3-base);">{{ selectedList }}</span>
          </div>
          
          <div v-else class="mt-6 mb-6 tab-group" >
            <v-btn-toggle>
              <v-btn
                :class="{'v-btn--active': activeTab === 'keywords'}"
                @click="activeTab = 'keywords'"
              >
                Keywords
              </v-btn>
              <v-btn
                :class="{'v-btn--active': activeTab === 'lists'}"
                @click="activeTab = 'lists'"
              >
                Lists
              </v-btn>
            </v-btn-toggle>
          </div>

          <div
            v-if="activeTab === 'keywords' || (activeTab === 'lists' && !!selectedList)"
            class="diff-section d-inline-flex"
          >
            <div class="diff-item">
              <div class="item-label">Improved</div>
              <div :class="{'item-value': true, 'green21Color--text': improvedCount > 0}">{{ improvedCount }}</div>
            </div>
            <div class="diff-item">
              <div class="item-label">Declined</div>
              <div :class="{'item-value': true, 'red11Color--text': declinedCount > 0}">{{ declinedCount }}</div>
            </div>
            <div v-for="range in [[1, 3], [4, 5], [11, 20], [20, 999]]" :key="String(range)" class="diff-item">
              <div class="item-label">Rank {{range[0]}}<template v-if="range[1] <= 20">-{{range[1]}}</template><template v-else>+</template></div>
              <div class="item-value">{{ getRankRangeCount(range[0], range[1]) }} <span v-if="getRankRangeDiff(range[0], range[1])" :class="{'item-value-diff': true, 'green21Color--text': getRankRangeDiff(range[0], range[1]) > 0, 'red11Color--text': getRankRangeDiff(range[0], range[1]) < 0}">{{ getRankRangeDiff(range[0], range[1]) > 0 ?'+':'' }}{{ getRankRangeDiff(range[0], range[1]) }}</span></div>
            </div>
          </div>

        </div>

        <div>

          <div class="d-flex align-center justify-end gap10 mb-8">
            <div class="d-flex align-center justify-center" style="background-color: #9E9FA1; border-radius:100%; padding:3px;">
              <i class="material-icons-outlined" style="color: #fff; font-size:1.2rem;">loop</i>
            </div>
            <div>
              <div class="mb-1" style="color: var(--v-blackColor-base); font-weight: 700; font-size:0.9rem;">Next update:</div>
              <div style="color: var(--v-grayColor50-base); font-weight: 400;">{{ toLocalDateString(keywordTrack.nextTrackDate) }}</div>
            </div>
          </div>
          
          <div v-if="!(isWhiteGloveUser && !isMissionControlSession)" class="d-flex gap15">
            
            <v-btn
              v-if="activeTab === 'lists' && !selectedList"
              color="profileAnchorColor"
              class="whiteColor--text text-none"
              @click="showNewListModal = true; newListFieldValue = '';">
              <i class="material-icons-outlined" style="margin-left: -8px;">add</i>
              <span style="font-weight: 700;">New list</span>
            </v-btn>
            
            <div v-if="activeTab === 'keywords' || activeTab === 'lists' && !!selectedList">
              <div class="d-flex align-center mb-2" style="gap: 8px; background-color: var(--v-whiteColor-base); padding: 8px 14px; border-radius: 8px; border: 1px solid var(--v-grayColor-base);">
                <vue-toggles
                  v-if="activeTab === 'keywords'"
                  :value="keywordTrack.sendEmailNotification == 1"
                  width="26"
                  height="16"
                  :checkedBg="$vuetify.theme.currentTheme['green21Color']"
                  :uncheckedBg="$vuetify.theme.currentTheme['grayColor']"
                  @click="updateKeywordTrack({sendEmailNotification: keywordTrack.sendEmailNotification == 1 ? 0 : 1})"
                >
                </vue-toggles>
                <vue-toggles
                  v-else-if="activeTab === 'lists' && !!selectedList"
                  :value="keywordTrack.keywordLists?.filter(list => list.name === selectedList)?.[0]?.send_email_notification == 1"
                  width="26"
                  height="16"
                  :checkedBg="$vuetify.theme.currentTheme['green21Color']"
                  :uncheckedBg="$vuetify.theme.currentTheme['grayColor']"
                  @click="updateKeywordTrack({keywordLists: keywordTrack.keywordLists.map(list => (
                    list?.name === selectedList
                    ?{
                      ...list,
                      send_email_notification: list.send_email_notification ? 0 : 1
                    }
                    :list
                  ))})"
                >
                </vue-toggles>
                <span class="mainColor3--text" style="font-size: 0.9rem;"> Email me monthly updates</span>
              </div>
              <div style="color: var(--v-gray50Color-base);">
                Need to send to more emails? <a
                  style="color: #1E85FF; text-decoration: underline;"
                  href="#"
                  @click.prevent="showEmailsModal = true"
                >Add emails</a>
              </div>
            </div>
          </div>
        </div>

      </div>

      <template v-if="activeTab === 'lists' && !selectedList">
        <div class="white-common-box mt-2 mb-10">
          <div
            class="d-flex align-center justify-space-between gap10"
            style="padding: 20px;"
          >
            <div>
              <div style="color: var(--v-blackColor-base); font-size: 1.1rem; font-weight: 700;">
                {{ keywordTrack?.keywordLists?.length || 0 }} List{{(keywordTrack?.keywordLists?.length) === 1 ? '':'s'}}
              </div>
            </div>
          </div>

          <div
            v-for="list in keywordTrack?.keywordLists" :key="list?.name"
            class="d-flex align-center gap10 justify-space-between"
            style="padding: 20px; border-top: 1px solid var(--v-grayColor-base);"
          >
            <div
              style="cursor: pointer;"
              @click="selectedList = list.name"
            >
              <div class="mb-1" style="color: var(--v-mainColor3-base); font-size: 1rem;">{{list.name}}</div>
              <div style="color: var(--v-gray50Color-base); font-size: 0.9rem;">{{ list?.keywords?.length || 0 }} Keyword{{(list?.keywords?.length) === 1 ? '':'s'}}</div>
            </div>
            <div v-if="!(isWhiteGloveUser && !isMissionControlSession)" class="d-flex align-center gap10">
              <v-tooltip top color="black">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn outlined
                    small
                    color="grayColor" class="button-with-icon text-none" style="min-width: initial; padding: 0 4px;"
                    @click="editListFieldValue = list.name; showEditListModalFor = list.name;"
                    v-bind="attrs" v-on="on"
                  >
                    <i class="material-icons-outlined" style="font-size: 1.1rem; color: var(--v-gray2Color-base);">edit</i>
                  </v-btn>
                </template>
                <div>Edit name</div>
              </v-tooltip>

              <v-tooltip top color="black">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn outlined
                    small
                    color="grayColor" class="button-with-icon text-none" style="min-width: initial; padding: 0 4px;"
                    @click="showDeleteListModalFor = list.name"
                    v-bind="attrs" v-on="on"
                  >
                    <i class="material-icons-outlined" style="font-size: 1.1rem; color: var(--v-gray2Color-base);">delete</i>
                  </v-btn>
                </template>
                <div>Delete list</div>
              </v-tooltip>
            </div>
          </div>
        </div>
      </template>
      
      <template v-if="activeTab === 'keywords' || (activeTab === 'lists' && !!selectedList)">
        <div class="white-common-box mt-5 mb-10">

          <div
            class="d-flex align-center justify-space-between gap10"
            style="padding: 15px 20px; border-bottom: 1px solid var(--v-grayColor-base);"
          >
            <div>
              <div style="color: var(--v-blackColor-base); font-weight: 700;">
                {{ trackKeywordRows?.length || 0 }} Keyword{{(trackKeywordRows?.length) === 1 ? '':'s'}}
              </div>
            </div>

            <div class="d-flex align-center gap10">
              <v-text-field
                class="table-search-field"
                outlined dense hide-details
                label="Search keywords"
                prepend-inner-icon="search"
                style="max-width: 300px; margin-top: -3px;"
                v-model="search"
              />

              <div>
                <v-btn
                  outlined
                  color="grayColor"
                  :id="`dot-download-${keywordTrack.id}`"
                  class="text-none"
                >
                  <span class="material-icons-outlined" style="color: var(--v-gray2Color-base); font-size: 1.2rem; margin-right: 6px;">file_download</span>
                  <span style="color: var(--v-mainColor3-base);">Download</span>
                </v-btn>
                <v-menu
                  :activator="`#dot-download-${keywordTrack.id}`"
                  bottom
                  offset-y
                >
                  <v-list>
                    <v-list-item link>
                      <v-list-item-title
                        class="mainColor--text"
                        @click="downloadAsCSV"
                        >Downlaod as CSV</v-list-item-title
                      >
                    </v-list-item>
                    <v-list-item link>
                      <v-list-item-title
                        class="mainColor--text"
                        @click="downloadAsPDF"
                        >Download as PDF</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>

          <horizontal-two-scroll-bar style="background-color: var(--v-pink1Color-base);">
            <table class="keyword-tracking-table" :id="tableId">
              <thead>
                <tr>
                  <th style="text-align: left;">Tracked keyword</th>
                  <th style="text-align: left;">Latest rank</th>
                  <th style="text-align: left;">Previous rank</th>
                  <th style="text-align: left;">Change</th>
                  <th style="text-align: left;">Monthly Vol, Low to High</th>
                  <th style="text-align: left;">Current ranking URL</th>
                  <th style="text-align: left;">Last checked</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in trackKeywordRows" :key="idx" @mouseover="currentHoveredIdx = idx" @mouseleave="currentHoveredIdx = null">
                  <td>
                    <div class="keyword-col d-flex align-center justify-space-between gap10">

                      <div class="d-flex align-center gap10">
                        <i
                          v-if="keywordTrack.favoriteKeywords.includes(item.keyword)"
                          class="material-icons-outlined"
                          style="color: #FFC745; cursor: pointer;"
                          @click="!(isWhiteGloveUser && !isMissionControlSession) ? updateKeywordTrack({favoriteKeywords: keywordTrack.favoriteKeywords.filter(k => k !== item.keyword)}) : null"
                        >star</i>
                        <i
                          v-else
                          class="material-icons-outlined"
                          style="color: var(--v-gray9Color-base); cursor: pointer;"
                          @click="!(isWhiteGloveUser && !isMissionControlSession) ? updateKeywordTrack({favoriteKeywords: [...keywordTrack.favoriteKeywords, item.keyword]}) : null"
                        >star_border</i>
   
                        <div style="font-weight: 700;">
                          {{ item.keyword }}
                        </div>
                      </div>

                      <div
                        v-if="!(isWhiteGloveUser && !isMissionControlSession)"
                        class="keyword-menu-wrap" :style="currentHoveredIdx !== idx?'visibility:hidden;':''">
                        <v-menu bottom offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn outlined x-small color="gray9Color" v-bind="attrs"
                              v-on="on">
                              <span
                                class="material-icons-outlined mainColor3--text"
                                style="font-size: 20px"
                                >more_horiz</span
                              >
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item v-if="!(isWhiteGloveUser && !isMissionControlSession)" @click="gotoNewPopReport(item)">
                              <v-list-item-title>
                                Create POP report
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="keywordToListMap[item.keyword.toLowerCase()]" @click="updateKeywordTrack({keywordLists: keywordTrack.keywordLists.map(list => (
                              list?.keywords?.includes?.(item.keyword)
                              ?{
                                ...list,
                                keywords: list.keywords.filter(k => k !== item.keyword)
                              }
                              :list
                            ))})">
                              <v-list-item-title>
                                Remove from list
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-else @click="showAddKeywordToListModalFor = item.keyword; selectListFieldValue = null;">
                              <v-list-item-title>
                                Add to list
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="untrackKeywordAction(item)">
                              <v-list-item-title class="red11Color--text">
                                Remove
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                      
                    </div>
                  </td>
                  <td>
                    {{ item.currentRankGoogle }}
                  </td>
                  <td>
                    {{ item.oldRank || "None" }}
                  </td>
                  <td>
                    <div
                      :class="{'tag': true, 'red11Color': item.rankDiff && item.rankDiff > 0, 'green21Color': item.rankDiff && item.rankDiff < 0}"
                      style="font-weight: 700; color: #fff; display: inline-flex; align-items: center; gap: 4px; padding-left: 10px; padding-right: 10px;"
                    >
                      <span
                        v-if="item.rankDiff && item.rankDiff < 0"
                        class="material-icons-outlined"
                        style="color: #fff; font-size: 1.2rem;"
                        >arrow_upward</span
                      >
                      <span
                        v-if="item.rankDiff && item.rankDiff > 0"
                        class="material-icons-outlined"
                        style="color: #fff; font-size: 1.2rem;"
                        >arrow_downward</span
                      >
                      <span :class="{'mainColor3--text': !item?.rankDiff}">{{ item.rankDiff ? Math.abs(item.rankDiff) : "None" }}</span>
                    </div>
                  </td>
                  <td>
                    {{ item.broadMatchMonthlySearches_LowEstimate }} -
                    {{ item.broadMatchMonthlySearches_HighEstimate }}
                  </td>
                  <td>
                    <a
                      v-if="item.currentRankingUrl"
                      :href="item.currentRankingUrl"
                      class="open-url-link"
                      >{{ item.currentRankingUrl }}</a
                    >
                  </td>
                  <td>
                    {{ toLocalDateString(keywordTrack.oldTrackDate) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </horizontal-two-scroll-bar>
        </div>
      </template>

      <div v-if="showNewListModal" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showNewListModal = false; newListFieldValue = '';"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor3--text mb-8">Create a new keyword tracking list</p>

              <div>
                <div
                  class="mb-2"
                  style="font-size: 0.9rem; font-weight: 700; color: var(--v-mainColor3-base);"
                >
                  Name
                </div>

                <v-text-field
                  v-model="newListFieldValue"
                  :autofocus="true"
                  placeholder="Enter list name"
                  hide-details
                  outlined
                  dense
                  color="grayColor"
                />
              </div>

              <div class="d-flex justify-end gap10 mt-8">
                <v-btn
                  outlined color="grayColor" class="text-none"
                  @click="showNewListModal = false; newListFieldValue = '';"
                >
                  <span style="color: var(--v-mainColor3-base);">Cancel</span>
                </v-btn>
                <v-btn color="profileAnchorColor"
                  :disabled="!newListFieldValue || keywordTrack.keywordLists.map(list => list.name.toLowerCase()).includes(newListFieldValue.toLowerCase())"
                  class="whiteColor--text text-none"
                  @click.stop="updateKeywordTrack({keywordLists: [
                    ...keywordTrack.keywordLists,
                    {
                      name: newListFieldValue,
                      keywords: []
                    }
                  ]})">
                  <span style="font-weight: 700;">Save</span>
                </v-btn>
                
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="showNewListModal = false; newListFieldValue = '';">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div v-if="showEditListModalFor" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showEditListModalFor = null;"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor3--text mb-8">Edit list</p>

              <div>
                <div
                  class="mb-2"
                  style="font-size: 0.9rem; font-weight: 700; color: var(--v-mainColor3-base);"
                >
                  Name
                </div>

                <v-text-field
                  v-model="editListFieldValue"
                  :autofocus="true"
                  placeholder="Enter list name"
                  hide-details
                  outlined
                  dense
                  color="grayColor"
                />
              </div>

              <div class="d-flex justify-end gap10 mt-8">
                <v-btn
                  outlined color="grayColor" class="text-none"
                  @click="showEditListModalFor = null;"
                >
                  <span style="color: var(--v-mainColor3-base);">Cancel</span>
                </v-btn>
                <v-btn color="profileAnchorColor"
                  :disabled="!editListFieldValue || (editListFieldValue !== showEditListModalFor && keywordTrack.keywordLists.map(list => list.name.toLowerCase()).includes(editListFieldValue.toLowerCase()))"
                  class="whiteColor--text text-none"
                  @click.stop="updateKeywordTrack({keywordLists: keywordTrack.keywordLists.map(list => (
                      list.name.toLowerCase() === showEditListModalFor.toLowerCase()
                      ?{
                        ...list,
                        name: editListFieldValue
                      }
                      :list
                    ))})">
                  <span style="font-weight: 700;">Save</span>
                </v-btn>
                
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="showEditListModalFor = null;">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div v-if="showAddKeywordToListModalFor" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showAddKeywordToListModalFor = null;"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor3--text mb-8">Add "{{showAddKeywordToListModalFor}}" to a list</p>

              <div
                class="mb-2"
                style="font-size: 1rem; font-weight: 400; color: var(--v-mainColor3-base); line-height: 1.6;"
              >
                Choose an existing list to add the keyword to:
              </div>

              <div>
                <div
                  class="mb-2"
                  style="font-size: 0.9rem; font-weight: 700; color: var(--v-mainColor3-base);"
                >
                  List
                </div>

                <v-select outlined dense hide-details
                  v-model="selectListFieldValue"
                  :items="keywordTrack.keywordLists.map(list => list.name)"
                ></v-select>
              </div>

              <div class="d-flex justify-end gap10 mt-8">
                <v-btn
                  outlined color="grayColor" class="text-none"
                  @click="showAddKeywordToListModalFor = null;"
                >
                  <span style="color: var(--v-mainColor3-base);">Cancel</span>
                </v-btn>
                <v-btn color="profileAnchorColor"
                  class="whiteColor--text text-none"
                  :disabled="!selectListFieldValue"
                  @click.stop="updateKeywordTrack({keywordLists: keywordTrack.keywordLists.map(list => (
                    list.name === selectListFieldValue
                    ?{
                      ...list,
                      keywords: [
                        ...list.keywords,
                        showAddKeywordToListModalFor
                      ]
                    }
                    :list
                  ))})">
                  <span style="font-weight: 700;">Add keyword</span>
                </v-btn>
                
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="showAddKeywordToListModalFor = null;">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div v-if="showDeleteListModalFor" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showDeleteListModalFor = null;"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor3--text mb-8">Delete "{{showDeleteListModalFor}}" list</p>

              <div
                class="mb-2"
                style="font-size: 1rem; font-weight: 400; color: var(--v-mainColor3-base); line-height: 1.6;"
              >
                You're about to delete this list. Keywords in this list won't be lost - they will still be visible in all keywords tab, ungrouped. 
              </div>

              <div class="d-flex justify-end gap10 mt-8">
                <v-btn
                  outlined color="grayColor" class="text-none"
                  @click="showDeleteListModalFor = null;"
                >
                  <span style="color: var(--v-mainColor3-base);">Cancel</span>
                </v-btn>
                <v-btn color="profileAnchorColor"
                  class="whiteColor--text text-none"
                  @click.stop="updateKeywordTrack({keywordLists: keywordTrack.keywordLists.filter(list => list.name.toLowerCase() !== showDeleteListModalFor.toLowerCase())})">
                  <span style="font-weight: 700;">Delete list</span>
                </v-btn>
                
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="showDeleteListModalFor = null;">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div v-if="showEmailsModal" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showEmailsModal = false"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor3--text">Add more emails</p>
              <div class="mainColor3--text message-text mb-4" style="font-size: 1rem; font-weight: 400; line-height: 150%;">
                By default, we send all tracking updates to your POP email <strong style="font-size: 1rem; line-height: 150%;">{{ ownerUser.email }}</strong>. You can add more addresses below if needed.<br />
              </div>

              <div>
                <div
                  class="mb-2"
                  style="font-size: 1rem; font-weight: 400; color: var(--v-mainColor3-base);"
                >
                  Email addresses <span style="padding:2px 6px; border: 1px solid var(--v-grayColor-base); border-radius: 18px; font-weight: normal; color: var(--v-mainColor3-base);">{{ emailsFieldValue.length }} / 3</span>
                </div>
                <v-combobox
                  multiple
                  outlined
                  hide-details
                  chips
                  deletable-chips
                  small-chips
                  append-icon=""
                  :delimiters="[' ', ',', ';']"
                  v-model="emailsFieldValue"
                  style="min-height: 60px;"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      class="my-2"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="emailsFieldValue = emailsFieldValue.filter(existing => data.item !== existing)"
                      @click:close="emailsFieldValue = emailsFieldValue.filter(existing => data.item !== existing)"
                    >
                      {{ data.item }}
                    </v-chip>
                  </template>
                </v-combobox>
              </div>

              <div class="d-flex gap10 mt-5">
                <v-btn color="profileAnchorColor"
                  :disabled="!isValidEmailsFieldValue"
                  class="whiteColor--text text-none"
                  @click.stop="updateKeywordTrack({emails: emailsFieldValue})">
                  <span style="font-weight: 700;">Save</span>
                </v-btn>
                
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="() => (showEmailsModal = false)">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

    </div>
    <div v-else>
      <div class="tool-header mb-10 mt-14">
        <div class="site-name">{{ site.name }}</div>
        <div class="tool-title">
          Keep track of keyword rank changes
          <span class="material-icons video-tutorial-icon video-tutorial-icon-size"
            @click="() => {
              $store.commit('setCurrentVideoTutorialToDisplay', {
                title: 'How to Use the Keyword Tracker Tool',
                videoLink: 'https://www.youtube.com/embed/ZvKyAujQJfc',
                description: 'In this video, you\'ll learn how to use POP\'s Keyword Tracker tool. The Keyword Tracker tool takes the guesswork out of monitoring your keyword progress without bogging you down with complicated dashboards. You\'ll receive straightforward updates on your keywords, including old rank, new rank, rank changes, monthly search volume, and the current ranking URL. Plus, with email updates, you\'re always in the loop about any ranking changes.',
                time: '1m 43s'
              });
            }">
            videocam
          </span>
        </div>
        <div class="tool-description">Track keyword rankings monthly with clear position changes, ranking URLs and email updates.</div>
      </div>

      <div class="container">
  
        <div
          class="white-common-box d-flex mb-14"
          style="padding: 60px 42px; gap: 28px;"
        >
    
          <div style="flex: 1; text-align: center;">
            <img src="/static/keyword-tracking-image.svg" style="width: 450px;">
          </div>
    
          <div style="flex: 1;">
            <div style="max-width: 450px;">
              <div
                class="mb-4 mt-4"
                style="font-size: 1.4rem; font-weight: 800; line-height: 1.6; color: var(--v-mainColor3-base);"
              >You haven’t added any keywords yet</div>
              <div
                class="mb-6"
                style="font-size: 1.1rem; line-height: 1.6; color: var(--v-mainColor3-base);"
              >Add keywords from Keyword Insight or Keyword Strategy tabs to be able to track their change over time. You will be redirected to Keyword Insight tab by default. Just click on the plus icon on the left side of the keywords to start tracking.</div>
              <v-btn color="profileAnchorColor"
                class="whiteColor--text text-none font-weight-bold mb-6"
                large
                @click="moveToKeywordInsight"
              >
                Go to Keyword insight
              </v-btn>
              <div style="font-size: 0.9rem; line-height: 1.6; color: var(--v-gray2Color-base); font-weight: 600;">
                You can track up to 250 keywords with an Unlimited plan, and up to 500 keywords with a Teams plan.
              </div>
            </div>
          </div>
    
        </div>
        
      </div>

    </div>
  </div>
</template>

<script>
import { toLocalDateString } from "@/utils";
import { mixin as export_mixin } from "@/components/export_mixin";
import HorizontalTwoScrollBar from "@/common/HorizontalTwoScrollBar.vue";
import { whiteGlovePlans } from "@/components/plans_constants"
import { getPureServicePkgName } from '@/utils'
import rockyKeywordScreenMixin, {
  KEYWORD_SCREENS,
  MAX_LIST_ITEMS,
  MAX_TABLE_ROWS,
  summarize,
  short as rockyShort,
} from '../rockyKeywordScreen.js';

export default {
  mixins: [export_mixin, rockyKeywordScreenMixin],
  components: {
    HorizontalTwoScrollBar,
  },
  props: ['site', 'currentCampaign'],
  data() {
    return {
      keywordTrack: null,
      search: '',
      showEmailsModal: false,
      savingEmails: false,
      emailsFieldValue: [],
      currentHoveredIdx: null,
      activeTab: 'keywords',
      selectedList: null,
      showNewListModal: false,
      newListFieldValue: '',
      showDeleteListModalFor: null,
      showEditListModalFor: null,
      editListFieldValue: '',
      showAddKeywordToListModalFor: null,
      selectListFieldValue: null,
    };
  },
  computed: {
    tableId() {
      return `track-table-${this.keywordTrack?.id || 0}`;
    },
    campaignId() {
      return parseInt(this.$route.params.campaignId || 0);
    },
    keywordToListMap() {
      const result = {}

      if (this.keywordTrack) {
        this.keywordTrack.keywordLists.forEach(list => {
          list.keywords.forEach(keyword => {
            result[keyword.toLowerCase()] = list.name
          })
        })
      }

      return result
    },
    trackKeywordRows() {
      let result = [];
      if (this.keywordTrack) {
        result = this.keywordTrack.trackResult;
      }

      if (this.selectedList) {
        result = result.filter(row => this.keywordToListMap[String(row.keyword).toLowerCase()] === this.selectedList)
      }

      const search = this.search ? this.search.toLowerCase() : ''

      if (search) {
        result = result.filter(row => String(row.keyword).toLowerCase().includes(search))
      }

      result = result.toSorted((a, b) => {
        const aVal = this.keywordTrack.favoriteKeywords.includes(a.keyword) ? 1 : 0;
        const bVal = this.keywordTrack.favoriteKeywords.includes(b.keyword) ? 1 : 0;
        if (aVal < bVal) {
          return 1;
        }
        if (aVal > bVal) {
          return -1;
        }
        return 0;
      })

      return result
        .filter((item) => item && typeof item === "object")
        .map((item) => {
          item["oldRank"] = this.getOldRank(item);
          item["rankDiff"] = this.getRankDiff(item);
          return item;
        });
    },
    oldTrackKeywordRows() {
      let result = [];
      if (this.keywordTrack) {
        result = this.keywordTrack.oldTrackResult;
      }
      return (result || []).filter((item) => item && typeof item === "object");
    },
    user() {
      return this.$store.state.user
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    ownerUser() {
      return this.$store.state.selectedSubAccountRelation || this.user
    },
    isValidEmailsFieldValue() {
      return (
        (this.emailsFieldValue || [])?.length <= 3
        && (this.emailsFieldValue || []).filter(email => !email.includes('@')).length === 0
      )
    },
    improvedCount() {
      return this.trackKeywordRows?.filter?.(item => item.rankDiff && item.rankDiff < 0)?.length
    },
    declinedCount() {
      return this.trackKeywordRows?.filter?.(item => item.rankDiff && item.rankDiff > 0)?.length
    },
  },
  watch: {
    campaignId: function (newVal, oldVal) {
      if (newVal) {
        this.loadKeywordTrack();
      }
    },
    '$store.state.triggerRockyRefresh': function () {
      this.handleRockyRefresh();
    },
    keywordTrack () { this.emitRockyKeywordScreen() },
    search () { this.emitRockyKeywordScreenDebounced() },
    selectedList () { this.emitRockyKeywordScreenDebounced() },
    activeTab () { this.emitRockyKeywordScreenDebounced() },
  },
  methods: {
    handleRockyRefresh() {
      if (this.campaignId) {
        this.loadKeywordTrack();
      }
    },
    // Rocky snapshot of the tracking table as rendered (list/search filters
    // applied, favorites first, oldRank/rankDiff merged in).
    buildRockyScreenData() {
      const track = this.keywordTrack;
      return {
        screen: KEYWORD_SCREENS.KEYWORD_TRACKING,
        status: track ? ((track.trackResult || []).length ? 'ready' : 'empty') : 'loading',
        loading: !track,
        recordIds: { keywordTrackId: track?.id },
        data: {
          rows: summarize(this.trackKeywordRows, (r) => ({
            keyword: rockyShort(r.keyword),
            currentPosition: r.currentRankGoogle != null ? r.currentRankGoogle : null,
            previousPosition: r.oldRank != null ? r.oldRank : null,
            rankChange: r.rankDiff != null ? r.rankDiff : null,
            currentRankingUrl: rockyShort(r.currentRankingUrl),
            monthlySearchesLow: r.broadMatchMonthlySearches_LowEstimate != null ? r.broadMatchMonthlySearches_LowEstimate : null,
            monthlySearchesHigh: r.broadMatchMonthlySearches_HighEstimate != null ? r.broadMatchMonthlySearches_HighEstimate : null,
            favorite: !!track?.favoriteKeywords?.includes(r.keyword),
            list: this.keywordToListMap[String(r.keyword).toLowerCase()] || null,
          }), MAX_TABLE_ROWS),
          summary: {
            trackedCount: (track?.trackResult || []).length,
            improvedCount: this.improvedCount ?? 0,
            declinedCount: this.declinedCount ?? 0,
            oldTrackDate: track?.oldTrackDate || null,
            nextTrackDate: track?.nextTrackDate || null,
          },
          lists: summarize(track?.keywordLists || [], (l) => ({
            name: rockyShort(l.name),
            keywordCount: (l.keywords || []).length,
          }), MAX_LIST_ITEMS),
          viewState: {
            search: rockyShort(this.search),
            selectedList: this.selectedList != null ? rockyShort(this.selectedList) : null,
            activeTab: this.activeTab,
          },
        },
      };
    },
    getRankRangeCount(from, to) {
      return this.trackKeywordRows?.filter?.(item => item.currentRankGoogle && item.currentRankGoogle >= from && item.currentRankGoogle <= to)?.length
    },
    getOldRankRangeCount(from, to) {
      return this.trackKeywordRows?.filter?.(item => item.oldRank && item.oldRank >= from && item.oldRank <= to)?.length
    },
    getRankRangeDiff(from, to) {
      return this.getRankRangeCount(from, to) - this.getOldRankRangeCount(from, to)
    },
    moveToKeywordInsight() {
      let currentRoute = this.$router.currentRoute.path.split("/");
      currentRoute[currentRoute.length - 1] = "keyword-insight";
      this.$router.push(currentRoute.join("/"));
    },
    toLocalDateString(d) {
      return toLocalDateString(d);
    },
    loadKeywordTrack() {
      this.$store
        .dispatch("getKeywordTrackForCampaign", this.campaignId)
        .then((response) => {
          if (response.data && response.data.status == "SUCCESS") {
            this.keywordTrack = response.data.keywordTrack;
            this.emailsFieldValue = response.data.keywordTrack?.emails || [];
          }
        });
    },
    getOldRank(item) {
      const oldResult = this.oldTrackKeywordRows.find(
        (r) => r.keyword == item.keyword
      );
      if (oldResult) {
        return oldResult.currentRankGoogle;
      } else {
        return null;
      }
    },
    getRankDiff(item) {
      const oldResult = this.oldTrackKeywordRows.find(
        (r) => r.keyword == item.keyword
      );
      if (oldResult) {
        const oldRank =
          typeof oldResult.currentRankGoogle == "number"
            ? oldResult.currentRankGoogle
            : 0;
        const newRank =
          typeof item.currentRankGoogle == "number"
            ? item.currentRankGoogle
            : 0;
        return newRank - oldRank;
      } else {
        return null;
      }
    },
    updateKeywordTrack(payload) {
      let data = {
        campaignId: this.campaignId,
        ...payload
      };

      this.$store.commit("showLoading");
      this.$store
        .dispatch("updateKeywordTrack", data)
        .then((response) => {
          if (response.data && response.data.status == "SUCCESS") {
            this.keywordTrack = response.data.keywordTrack;
            this.emailsFieldValue = response.data.keywordTrack?.emails || [];
            this.$notify({
              group: "info",
              type: "success",
              text: "Successfully saved",
            });
            if (this.showNewListModal) {
              this.showNewListModal = false;
              this.newListFieldValue = '';
            }
            if (this.showDeleteListModalFor) {
              this.showDeleteListModalFor = null;
            }
            if (this.showEditListModalFor) {
              this.showEditListModalFor = null;
            }
            if (this.showAddKeywordToListModalFor) {
              this.showAddKeywordToListModalFor = null;
            }
          } else {
            this.$notify({
              group: "info",
              type: "error",
              text: "Failed to save",
            });
          }
        })
        .finally(() => {
          this.$store.commit("hideLoading");
        });
    },
    untrackKeywordAction(item) {
      let data = {
        campaignId: this.campaignId,
        trackKeyword: item.keyword,
      };

      this.$store.commit("showLoading");
      this.$store
        .dispatch("removeKeywordFromTrack", data)
        .then((response) => {
          if (response.data && response.data.status == "SUCCESS") {
            this.keywordTrack = response.data.keywordTrack;
            this.$notify({
              group: "info",
              type: "success",
              text: "Successfully removed keyword from track.",
            });
          } else {
            this.$notify({
              group: "info",
              type: "error",
              text: "Failed to remove keyword from the track.",
            });
          }
        })
        .finally(() => {
          this.$store.commit("hideLoading");
        });
    },
    gotoNewPopReport(keywordReport) {
      const { siteId } = this.$route.params;
      const externalData = {
        keyword: keywordReport.keyword,
        lang: this.currentCampaign.languageCode,
        loc: this.currentCampaign.locationCode,
        isPageNotBuilt: !keywordReport.currentRankingUrl,
        url: keywordReport.currentRankingUrl,
      };

      this.$router.push({
        name: 'NewPage',
        params: {
          siteId,
          externalData,
        }
      });
    },
    downloadAsCSV() {
      console.log("cccc clicked");
      let rlt = [];
      rlt.push([
        "Tracked keyword",
        "Old rank",
        "New rank",
        "Change",
        "Monthly Volumn(Low-High)",
        "Current ranking URL",
      ]);
      this.trackKeywordRows.map((item) => {
        rlt.push([
          item.keyword,
          item.oldRank || "None",
          item.currentRankGoogle,
          item.rankDiff || "None",
          `${item.broadMatchMonthlySearches_LowEstimate} - ${item.broadMatchMonthlySearches_HighEstimate}`,
          item.currentRankingUrl,
        ]);
      });
      let csvDownloadData = rlt
        .map((item) => {
          return item.join(",");
        })
        .join("\n");
      this.exportToCSV(
        csvDownloadData,
        `track-keywords-${this.keywordTrack?.id || ""}.csv`
      );
    },
    downloadAsPDF() {
      const columns = [
        "Tracked keyword",
        "Old rank",
        "New rank",
        "Change",
        "Monthly Vol, Low to High",
        "Current ranking URL",
      ];
      const rows = this.trackKeywordRows.map((row) => [
        row.keyword,
        row.oldRank || "None",
        row.currentRankGoogle,
        row.rankDiff || "None",
        `${row.broadMatchMonthlySearches_LowEstimate} - ${row.broadMatchMonthlySearches_HighEstimate}`,
        row.currentRankingUrl || "",
      ]);

      this.generatePDF(
        columns,
        rows,
        `track-keywords-${this.keywordTrack?.id || ""}`
      );
    },
    startKeywordTrackAction() {
      this.$store.commit("showLoading");
      this.$store
        .dispatch("startKeywordTrack", {
          campaignId: this.campaignId,
        })
        .then((response) => {
          if (response.data && response.data.status == "SUCCESS") {
            this.keywordTrack = response.data.keywordTrack;
            this.$notify({
              group: "info",
              type: "success",
              text: "Started the track.",
            });
          } else {
            this.$notify({
              group: "info",
              type: "error",
              text: response.data.msg || "Failed",
            });
          }
        })
        .finally(() => {
          this.$store.commit("hideLoading");
        });
    },
  },
  mounted() {
    this.emitRockyKeywordScreen();
    if (this.campaignId && !this.keywordTrack) {
      this.loadKeywordTrack();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../keyword-research.scss";
.keyword-tracking-root {
  .h-line {
    height: 1px;
    background: var(--v-grayColor-base);
  }

  table {
    td {
      background-color: var(--v-whiteColor-base);
    }
    th {
      background: var(--v-pink1Color-base);
    }
  }
}

.tool-header {

    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    
    .site-name {
        color: var(--v-gray50Color-base);
        margin-bottom: 6px;
    }
    .tool-title {
        color: var(--v-mainColor3-base);
        font-size: 1.6rem;
        font-weight: 800;
        margin-bottom: 12px;
        line-height: 1.6;
    }
    .tool-description {
        color: var(--v-mainColor3-base);
        font-size: 1rem;
        max-width: 450px;
        line-height: 1.6;
    }

}

.diff-section {
  padding: 16px;
  gap: 30px;
  background-color: var(--v-whiteColor-base);
  border: 1px solid var(--v-grayColor-base);
  border-radius: 10px;

  .diff-item:not(:first-child) {
    padding-left: 8px;
    border-left: 1px solid var(--v-grayColor-base);
  }

  .item-label {
    color: var(--v-gray50Color-base);
    font-size: 0.9rem;
    font-weight: 400;
    margin-bottom: 10px;
  }
  .item-value {
    color: var(--v-blackColor-base);
    font-size: 1.3rem;
    font-weight: 800;
    display: flex;
    flex-direction: row;
    gap: 4px;
    .item-value-diff {
      color: var(--v-blackColor-base);
      font-size: 0.9rem;
      font-weight: 800;
    }
  }
}

::v-deep .table-search-field {
  .v-icon {
    color: var(--v-gray36Color-base);
  }
  .v-label {
    color: var(--v-gray36Color-base);
    font-size: 0.85rem;
  }
  fieldset {
    border-color: var(--v-gray12Color-base);
  }
}

.keyword-menu-wrap {
  .v-btn {
    height: 24px !important;
    min-width: 20px !important;
    padding: 0 !important;
  }
}

.tab-group {
  
  // NOTE: .v-btn class is repeated to give the css rule more weight
  .v-btn.v-btn.v-btn.v-btn {
    text-transform: none;
    border-color: #CCCCCC !important;
    background-color: var(--v-cardBg-base) !important;
    height: 38px !important;

    color: var(--v-mainColor3-base) !important;

    &.v-btn--active {
      border: 1.11px solid #FF5700 !important;
      background: #FFEDDD !important;
      color: #014751 !important;
      box-shadow: none !important;
      &::before {
        background: none !important;
      }
    }

    .v-btn__content {
      font-size: 0.95rem !important;
      font-weight: 500 !important;
      letter-spacing: normal;
    }
  }
}
</style>
